import { createEffect, createSignal, on, onCleanup, onMount } from "solid-js";
import { CELL_SIZE, easeInOutCubic, type PieceAnim, type ViewportBbox } from "../lib/cosmology";
import { useT } from "../i18n/LocaleProvider";

export interface ViewportInfo {
  worldCx: number;
  worldCy: number;
  worldHalf: number;
  zoom: number;
}

export interface CanvasPiece {
  id: string;
  imageUrl: string;
  thumbUrl?: string;
  mediumUrl?: string;
  x: number;
  y: number;
  likes?: number;
  anim?: PieceAnim;
  wasCentre?: boolean;
  isOwn?: boolean;
}

interface Props {
  onViewportChange?: (info: ViewportInfo) => void;
  pieces?: CanvasPiece[];
  likedIds?: Set<string>;
  onLike?: (id: string) => void;
  onOpen?: (id: string) => void;
  /** WEB37 — cell to centre on (deep-link pan). Zoom is preserved; the pan
   *  fires once per identity change, then `onFocusConsumed` runs. */
  focusCell?: { x: number; y: number } | null;
  onFocusConsumed?: () => void;
}

// Heart button sits at bottom-right of each cell.
const HEART_SIZE_RATIO = 0.26; // fraction of CELL_SIZE
const HEART_PAD_RATIO = 0.08; // inset from cell edge

function heartPos(cx: number, cy: number) {
  const hSize = CELL_SIZE * HEART_SIZE_RATIO;
  const hPad = CELL_SIZE * HEART_PAD_RATIO;
  return {
    hx: cx + CELL_SIZE * 0.5 - hPad - hSize * 0.5,
    hy: cy + CELL_SIZE * 0.5 - hPad - hSize * 0.5,
    hitR: hSize * 0.8,
  };
}

// Count label sits just to the LEFT of the heart icon.
function drawLikeCount(
  ctx: CanvasRenderingContext2D,
  hx: number,
  hy: number,
  hr: number,
  zoomLevel: number,
  count: number,
  liked: boolean,
) {
  const label = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count);
  // Font size lives in world units so it scales WITH the heart, but floor it
  // at ~10 screen pixels so it stays legible when zoomed out.
  const fontPx = Math.max(10 / zoomLevel, hr * 1.5);
  ctx.save();
  ctx.font = `700 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  const padX = hr * 0.5;
  const tx = hx - hr - padX;
  const ty = hy;
  // Stroke (dark halo) for readability over any image.
  ctx.lineWidth = Math.max(2 / zoomLevel, fontPx * 0.22);
  ctx.strokeStyle = "rgba(0,0,0,0.85)";
  ctx.strokeText(label, tx, ty);
  ctx.fillStyle = liked ? "#fbbf24" : "rgba(255,255,255,0.95)";
  ctx.fillText(label, tx, ty);
  ctx.restore();
}

// Modern bezier heart — cx,cy = center, r = half-height
function drawHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(cx, cy + r * 0.9);
  ctx.bezierCurveTo(cx - r * 0.4, cy + r * 0.5, cx - r, cy + r * 0.1, cx - r, cy - r * 0.38);
  ctx.bezierCurveTo(cx - r, cy - r * 0.9, cx - r * 0.45, cy - r, cx, cy - r * 0.48);
  ctx.bezierCurveTo(cx + r * 0.45, cy - r, cx + r, cy - r * 0.9, cx + r, cy - r * 0.38);
  ctx.bezierCurveTo(cx + r, cy + r * 0.1, cx + r * 0.4, cy + r * 0.5, cx, cy + r * 0.9);
  ctx.closePath();
}

// Centre piece visual scale: longest side = CELL_SIZE × this factor, natural aspect ratio (contain, no crop).
const CENTER_SCALE = 2.16;

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.12;
const HINT_IDLE_MS = 5000;

// Bumped from v9.1 — grid-only mode invalidates prior pan/zoom snapshots so
// every visitor lands with (0,0) at the screen centre on first paint.
const VIEW_STORAGE_KEY = "ikamiro:view:v9.3";
const RESET_EVENT = "ikamiro:reset-view";

type StoredView = { panX: number; panY: number; zoom: number };

function loadStoredView(): StoredView | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<StoredView>;
    if (
      typeof v.panX === "number" &&
      typeof v.panY === "number" &&
      typeof v.zoom === "number" &&
      v.zoom >= MIN_ZOOM &&
      v.zoom <= MAX_ZOOM
    ) {
      return { panX: v.panX, panY: v.panY, zoom: v.zoom };
    }
  } catch {
    // ignore — fall through to defaults
  }
  return null;
}

export default function CosmologyCanvas(props: Props) {
  const t = useT();
  let canvas!: HTMLCanvasElement;

  const [w, setW] = createSignal(typeof window === "undefined" ? 1200 : window.innerWidth);
  const [h, setH] = createSignal(typeof window === "undefined" ? 720 : window.innerHeight);
  const stored = loadStoredView();
  const [panX, setPanX] = createSignal(stored?.panX ?? 0);
  const [panY, setPanY] = createSignal(stored?.panY ?? 0);
  const [zoom, setZoom] = createSignal(stored?.zoom ?? 1);
  const [showNavHint, setShowNavHint] = createSignal(false);
  let navHintDismissed = false;

  interface CachedImage {
    img: HTMLImageElement;
    ready: boolean;
    src: string;
    /** Last frame this entry was visible (performance.now()). LRU key. */
    lastUsed: number;
    /** True once the original-fallback swap has been attempted. */
    fellBack: boolean;
  }
  const imageCache = new Map<string, CachedImage>();
  // I16 — decoded-image budget. 400 thumbs × ~15 KB ≈ 6 MB: enough for any
  // viewport + pan buffer at 10k-piece scale; offscreen entries evict LRU.
  const IMAGE_CACHE_CAP = 400;

  const ensureImage = (p: CanvasPiece, now: number): CachedImage => {
    // Thumb-first: mural tiles never need more than 256 px (max on-screen
    // tile is 360 css px at max zoom). Originals load only in the lightbox.
    const src = p.thumbUrl ?? p.mediumUrl ?? p.imageUrl;
    const existing = imageCache.get(p.id);
    if (existing && existing.src === src) {
      existing.lastUsed = now;
      return existing;
    }
    if (existing) {
      // URL rotated under us (wire update) — drop the stale entry.
      imageCache.delete(p.id);
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    const entry: CachedImage = { img, ready: false, src, lastUsed: now, fellBack: false };
    img.onload = () => {
      if (imageCache.get(p.id) === entry) entry.ready = true;
    };
    img.onerror = () => {
      // Proxy hiccup (cf.image down, key missing) → single attempt at the
      // full original so the tile never stays blank when bytes exist.
      if (imageCache.get(p.id) !== entry || entry.fellBack) return;
      entry.fellBack = true;
      if (entry.src === p.imageUrl) return;
      entry.src = p.imageUrl;
      entry.ready = false;
      img.src = p.imageUrl;
    };
    img.src = src;
    imageCache.set(p.id, entry);
    return entry;
  };

  /** Evict least-recently-visible entries over the cap. Never evicts the
   *  currently visible set, so panning back is instant while memory stays
   *  bounded at mural scale. */
  const evictOffscreen = (visible: ReadonlySet<string>): void => {
    if (imageCache.size <= IMAGE_CACHE_CAP) return;
    const candidates: Array<[string, number]> = [];
    for (const [id, e] of imageCache) {
      if (!visible.has(id)) candidates.push([id, e.lastUsed]);
    }
    candidates.sort((a, b) => a[1] - b[1]);
    const over = imageCache.size - IMAGE_CACHE_CAP;
    for (let i = 0; i < over && i < candidates.length; i++) {
      imageCache.delete(candidates[i][0]);
    }
  };

  // Prune-only: entries for pieces that left the catalog drop immediately.
  // Loading is viewport-driven inside draw() — nothing offscreen fetches.
  createEffect(() => {
    const list = props.pieces ?? [];
    const seen = new Set<string>();
    for (const p of list) seen.add(p.id);
    for (const id of imageCache.keys()) {
      if (!seen.has(id)) imageCache.delete(id);
    }
  });

  // WEB37 — deep-link pan: centre `focusCell` once per identity change.
  // Zoom is preserved; the viewport effect below then fetches the window
  // around the target, so the piece renders even when it was paged out.
  createEffect(
    on(
      () => props.focusCell,
      (cell) => {
        if (!cell) return;
        const z = zoom();
        setPanX(-cell.x * CELL_SIZE * z);
        setPanY(-cell.y * CELL_SIZE * z);
        props.onFocusConsumed?.();
      },
      { defer: true },
    ),
  );

  let rafId = 0;
  let pointerDown = false;
  let dragging = false;
  let downX = 0;
  let downY = 0;
  let dragLastX = 0;
  let dragLastY = 0;
  let hoverPieceId: string | null = null;
  let hoverHeartId: string | null = null;
  // WEB61 — active pointers for pinch-zoom (touch). Single-pointer drag
  // and tap behave exactly as before.
  const activePointers = new Map<number, { x: number; y: number }>();
  let pinching = false;
  let lastPinchDist = 0;

  const toWorld = (clientX: number, clientY: number): { wx: number; wy: number } => {
    const rect = canvas.getBoundingClientRect();
    return {
      wx: (clientX - rect.left - w() / 2 - panX()) / zoom(),
      wy: (clientY - rect.top - h() / 2 - panY()) / zoom(),
    };
  };

  const pieceAt = (wx: number, wy: number): CanvasPiece | null => {
    const cellX = Math.round(wx / CELL_SIZE);
    const cellY = Math.round(wy / CELL_SIZE);
    const found = (props.pieces ?? []).find((p) => p.x === cellX && p.y === cellY);
    if (found) return found;
    // Centre piece may extend beyond its own cell — check its full bounding box.
    const cp = (props.pieces ?? []).find((p) => p.x === 0 && p.y === 0);
    if (cp) {
      const half = (CELL_SIZE * CENTER_SCALE) / 2;
      if (Math.abs(wx) <= half && Math.abs(wy) <= half) return cp;
    }
    return null;
  };

  const heartRect = (p: CanvasPiece): { hx: number; hy: number; hitR: number } => {
    const pCx = p.x * CELL_SIZE;
    const pCy = p.y * CELL_SIZE;
    if (p.x !== 0 || p.y !== 0) return heartPos(pCx, pCy);
    const cached = imageCache.get(p.id);
    const iw = cached?.ready && cached.img.naturalWidth > 0 ? cached.img.naturalWidth : 1;
    const ih = cached?.ready && cached.img.naturalHeight > 0 ? cached.img.naturalHeight : 1;
    const maxDim = CELL_SIZE * CENTER_SCALE;
    const aspect = iw / ih;
    const imgDisplayW = aspect >= 1 ? maxDim : maxDim * aspect;
    const imgDisplayH = aspect >= 1 ? maxDim / aspect : maxDim;
    const hSize = CELL_SIZE * HEART_SIZE_RATIO;
    const hPad = CELL_SIZE * HEART_PAD_RATIO;
    return {
      hx: pCx + imgDisplayW / 2 - hPad - hSize / 2,
      hy: pCy + imgDisplayH / 2 - hPad - hSize / 2,
      hitR: hSize * 0.8,
    };
  };

  /** Refresh hover hit-state for a client point. Runs on down (so touch
   *  taps hit without a prior hover), on move, and on up. */
  const updateHover = (clientX: number, clientY: number): void => {
    const { wx, wy } = toWorld(clientX, clientY);
    const found = pieceAt(wx, wy);
    hoverPieceId = found?.id ?? null;
    if (!found) {
      hoverHeartId = null;
      return;
    }
    const { hx, hy, hitR } = heartRect(found);
    hoverHeartId = Math.abs(wx - hx) < hitR && Math.abs(wy - hy) < hitR ? found.id : null;
  };

  const zoomAtScreenPoint = (mx: number, my: number, factor: number): void => {
    const oldZoom = zoom();
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom * factor));
    if (newZoom === oldZoom) return;
    const worldX = (mx - w() / 2 - panX()) / oldZoom;
    const worldY = (my - h() / 2 - panY()) / oldZoom;
    setZoom(newZoom);
    setPanX(mx - w() / 2 - worldX * newZoom);
    setPanY(my - h() / 2 - worldY * newZoom);
  };

  const onPointerDown = (e: PointerEvent) => {
    // Mouse right/middle click never starts a gesture; touch/pen always do.
    if (e.button !== 0 && e.pointerType === "mouse") return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (activePointers.size >= 2) {
      // Second finger down → pinch takes over; cancel the in-flight drag.
      pinching = true;
      dragging = false;
      pointerDown = false;
      const [a, b] = [...activePointers.values()];
      lastPinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      return;
    }
    pointerDown = true;
    dragging = false;
    downX = e.clientX;
    downY = e.clientY;
    dragLastX = e.clientX;
    dragLastY = e.clientY;
    updateHover(e.clientX, e.clientY);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (activePointers.has(e.pointerId)) {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinching && activePointers.size >= 2) {
      const [a, b] = [...activePointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (lastPinchDist > 0 && dist > 0) {
        const factor = Math.max(0.5, Math.min(2, dist / lastPinchDist));
        const rect = canvas.getBoundingClientRect();
        zoomAtScreenPoint((a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top, factor);
      }
      lastPinchDist = dist;
      return;
    }
    // Hover tracking — runs even without button down.
    updateHover(e.clientX, e.clientY);

    if (!pointerDown) return;
    if (!dragging && Math.hypot(e.clientX - downX, e.clientY - downY) > 4) {
      dragging = true;
      hoverPieceId = null; // clear hover while panning
      hoverHeartId = null;
    }
    if (dragging) {
      setPanX(panX() + (e.clientX - dragLastX));
      setPanY(panY() + (e.clientY - dragLastY));
      dragLastX = e.clientX;
      dragLastY = e.clientY;
    }
  };
  const onPointerLeave = () => {
    hoverPieceId = null;
    hoverHeartId = null;
  };
  const onPointerUp = (e: PointerEvent) => {
    activePointers.delete(e.pointerId);
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (pinching) {
      if (activePointers.size < 2) {
        // Pinch over — re-baseline the surviving finger so the canvas
        // does not jump if the user keeps dragging.
        pinching = false;
        lastPinchDist = 0;
        const rest = [...activePointers.values()][0];
        if (rest) {
          downX = rest.x;
          downY = rest.y;
          dragLastX = rest.x;
          dragLastY = rest.y;
        }
        pointerDown = activePointers.size === 1;
        dragging = false;
      }
      return;
    }
    const wasClick = pointerDown && !dragging;
    pointerDown = false;
    dragging = false;
    if (wasClick) {
      // Re-hit at release: touch taps never hover, so the down-hit may be
      // stale after a micro-move; the up-point is authoritative.
      updateHover(e.clientX, e.clientY);
      if (hoverPieceId) {
        if (hoverHeartId === hoverPieceId) {
          props.onLike?.(hoverPieceId);
        } else {
          props.onOpen?.(hoverPieceId);
        }
      }
    }
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    zoomAtScreenPoint(
      e.clientX - rect.left,
      e.clientY - rect.top,
      e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP,
    );
  };
  const onDblClick = () => {
    setPanX(0);
    setPanY(0);
    setZoom(1);
  };

  const zoomAtCenter = (factor: number) => {
    const oldZoom = zoom();
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom * factor));
    if (newZoom === oldZoom) return;
    const worldX = -panX() / oldZoom;
    const worldY = -panY() / oldZoom;
    setZoom(newZoom);
    setPanX(-worldX * newZoom);
    setPanY(-worldY * newZoom);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) {
      return;
    }
    const stepFrac = e.shiftKey ? 1 : 1 / 3;
    let handled = true;
    switch (e.key) {
      case "ArrowLeft":
        setPanX(panX() + w() * stepFrac);
        break;
      case "ArrowRight":
        setPanX(panX() - w() * stepFrac);
        break;
      case "ArrowUp":
        setPanY(panY() + h() * stepFrac);
        break;
      case "ArrowDown":
        setPanY(panY() - h() * stepFrac);
        break;
      case "PageUp":
        zoomAtCenter(ZOOM_STEP);
        break;
      case "PageDown":
        zoomAtCenter(1 / ZOOM_STEP);
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      if (!navHintDismissed) {
        navHintDismissed = true;
        setShowNavHint(false);
      }
    }
  };

  const onResize = () => {
    setW(window.innerWidth);
    setH(window.innerHeight);
  };

  const drawGrid = (ctx: CanvasRenderingContext2D, view: ViewportBbox) => {
    const minX = Math.floor(view.minX / CELL_SIZE) - 1;
    const maxX = Math.ceil(view.maxX / CELL_SIZE) + 1;
    const minY = Math.floor(view.minY / CELL_SIZE) - 1;
    const maxY = Math.ceil(view.maxY / CELL_SIZE) + 1;
    const z = zoom();
    const half = CELL_SIZE / 2;
    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    ctx.lineWidth = 1 / z;
    ctx.beginPath();
    for (let gx = minX; gx <= maxX + 1; gx++) {
      const x = gx * CELL_SIZE - half;
      ctx.moveTo(x, minY * CELL_SIZE - half);
      ctx.lineTo(x, (maxY + 1) * CELL_SIZE - half);
    }
    for (let gy = minY; gy <= maxY + 1; gy++) {
      const y = gy * CELL_SIZE - half;
      ctx.moveTo(minX * CELL_SIZE - half, y);
      ctx.lineTo((maxX + 1) * CELL_SIZE - half, y);
    }
    ctx.stroke();
    ctx.restore();
  };

  const draw = () => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const now = performance.now();
    const dpr = window.devicePixelRatio || 1;
    const W = w();
    const H = h();
    const targetW = Math.round(W * dpr);
    const targetH = Math.round(H * dpr);
    if (canvas.width !== targetW) canvas.width = targetW;
    if (canvas.height !== targetH) canvas.height = targetH;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    ctx.translate(W / 2 + panX(), H / 2 + panY());
    ctx.scale(zoom(), zoom());

    const z = zoom();
    const view: ViewportBbox = {
      minX: (-W / 2 - panX()) / z,
      maxX: (W / 2 - panX()) / z,
      minY: (-H / 2 - panY()) / z,
      maxY: (H / 2 - panY()) / z,
    };

    drawGrid(ctx, view);

    const list = props.pieces ?? [];
    if (list.length > 0) {
      const half = CELL_SIZE / 2;
      const minCellX = view.minX / CELL_SIZE - 1;
      const maxCellX = view.maxX / CELL_SIZE + 1;
      const minCellY = view.minY / CELL_SIZE - 1;
      const maxCellY = view.maxY / CELL_SIZE + 1;
      // I16 — viewport-driven loading: only visible pieces (+1 cell
      // buffer from the min/maxCell pad) fetch bytes; the rest never hit
      // the network. `visible` feeds the LRU evictor below.
      const visible = new Set<string>();
      for (const p of list) {
        if (p.x < minCellX || p.x > maxCellX) continue;
        if (p.y < minCellY || p.y > maxCellY) continue;
        visible.add(p.id);
        const cached = ensureImage(p, now);
        // Interpolate from anim.from* to target (x,y) during movement.
        let cx: number;
        let cy: number;
        if (p.anim) {
          const elapsed = now - p.anim.startMs;
          const t = easeInOutCubic(Math.min(1, elapsed / p.anim.durationMs));
          cx = (p.anim.fromX + (p.x - p.anim.fromX) * t) * CELL_SIZE;
          cy = (p.anim.fromY + (p.y - p.anim.fromY) * t) * CELL_SIZE;
        } else {
          cx = p.x * CELL_SIZE;
          cy = p.y * CELL_SIZE;
        }
        const isLiked = props.likedIds?.has(p.id) ?? false;
        const isHovered = hoverPieceId === p.id;

        // Border color priority: was_centre > own > liked > normal.
        const borderColor = p.wasCentre
          ? "#c084fc"  // purple — was at centre
          : p.isOwn
          ? "#22d3ee"  // cyan — created by me
          : isLiked
          ? "#f59e0b"  // amber — I liked it
          : "#ffffff"; // white — normal

        if (cached && cached.ready) {
          const iw = cached.img.naturalWidth;
          const ih = cached.img.naturalHeight;
          if (iw > 0 && ih > 0) {
            const isHeartHov = hoverHeartId === p.id;
            const border = 2 / z;
            const hSize = CELL_SIZE * HEART_SIZE_RATIO;
            const hr = hSize * 0.42; // heart half-height radius

            if (p.x === 0 && p.y === 0) {
              // Centre piece: natural aspect ratio (contain, no crop).
              const maxDim = CELL_SIZE * CENTER_SCALE;
              const aspect = iw / ih;
              const imgDisplayW = aspect >= 1 ? maxDim : maxDim * aspect;
              const imgDisplayH = aspect >= 1 ? maxDim / aspect : maxDim;
              // Border — glow when heart is hovered.
              ctx.save();
              if (isHeartHov) {
                ctx.shadowColor = borderColor;
                ctx.shadowBlur = 10 / z;
              }
              ctx.fillStyle = borderColor;
              ctx.fillRect(cx - imgDisplayW / 2 - border, cy - imgDisplayH / 2 - border, imgDisplayW + 2 * border, imgDisplayH + 2 * border);
              ctx.restore();
              ctx.drawImage(cached.img, cx - imgDisplayW / 2, cy - imgDisplayH / 2, imgDisplayW, imgDisplayH);
              if ((isHovered && !pointerDown) || isLiked) {
                ctx.save();
                const hPad = CELL_SIZE * HEART_PAD_RATIO;
                const hx = cx + imgDisplayW / 2 - hPad - hSize / 2;
                const hy = cy + imgDisplayH / 2 - hPad - hSize / 2;
                drawHeart(ctx, hx, hy, hr);
                if (isLiked) {
                  ctx.fillStyle = isHeartHov ? "#fbbf24" : "#f59e0b";
                  ctx.fill();
                } else if (isHeartHov) {
                  ctx.fillStyle = "rgba(255,255,255,0.95)";
                  ctx.fill();
                } else {
                  ctx.strokeStyle = "rgba(255,255,255,0.75)";
                  ctx.lineWidth = 1.5 / z;
                  ctx.stroke();
                }
                const likes = p.likes ?? 0;
                if (likes > 0 && (isLiked || isHovered)) {
                  drawLikeCount(ctx, hx, hy, hr, z, likes, isLiked);
                }
                ctx.restore();
              }
            } else {
              // Regular piece: cover (fills cell), colored border.
              ctx.save();
              if (isHeartHov) {
                ctx.shadowColor = borderColor;
                ctx.shadowBlur = 10 / z;
              }
              ctx.fillStyle = borderColor;
              ctx.fillRect(cx - half, cy - half, CELL_SIZE, CELL_SIZE);
              ctx.restore();
              const imgW = CELL_SIZE - 2 * border;
              const imgH = CELL_SIZE - 2 * border;
              const s = Math.max(imgW / iw, imgH / ih);
              const dw = iw * s;
              const dh = ih * s;
              ctx.save();
              ctx.beginPath();
              ctx.rect(cx - imgW / 2, cy - imgH / 2, imgW, imgH);
              ctx.clip();
              ctx.drawImage(cached.img, cx - dw / 2, cy - dh / 2, dw, dh);
              ctx.restore();
              if ((isHovered && !pointerDown) || isLiked) {
                ctx.save();
                const { hx, hy } = heartPos(cx, cy);
                drawHeart(ctx, hx, hy, hr);
                if (isLiked) {
                  ctx.fillStyle = isHeartHov ? "#fbbf24" : "#f59e0b";
                  ctx.fill();
                } else if (isHeartHov) {
                  ctx.fillStyle = "rgba(255,255,255,0.95)";
                  ctx.fill();
                } else {
                  ctx.strokeStyle = "rgba(255,255,255,0.75)";
                  ctx.lineWidth = 1.5 / z;
                  ctx.stroke();
                }
                const likes = p.likes ?? 0;
                if (likes > 0 && (isLiked || isHovered)) {
                  drawLikeCount(ctx, hx, hy, hr, z, likes, isLiked);
                }
                ctx.restore();
              }
            }

            continue;
          }
        }
        ctx.save();
        ctx.fillStyle = "#000000";
        ctx.fillRect(cx - half, cy - half, CELL_SIZE, CELL_SIZE);
        ctx.restore();
      }
      evictOffscreen(visible);
    }

    // Vignette: transparent at centre, fades to black at screen edges (screen space).
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const vCx = W / 2;
    const vCy = H / 2;
    const vR = Math.max(W, H) * 0.72;
    const vignette = ctx.createRadialGradient(vCx, vCy, 0, vCx, vCy, vR);
    vignette.addColorStop(0.0, "rgba(0,0,0,0)");
    vignette.addColorStop(0.38, "rgba(0,0,0,0)");
    vignette.addColorStop(1.0, "rgba(0,0,0,1)");
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);

    canvas.style.cursor = pointerDown ? "grabbing" : hoverPieceId ? "pointer" : "grab";
  };

  const tick = () => {
    draw();
    rafId = requestAnimationFrame(tick);
  };

  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const view: StoredView = { panX: panX(), panY: panY(), zoom: zoom() };
    if (typeof window === "undefined") return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(view));
      } catch {
        // quota / private mode — silently drop
      }
    }, 200);
  });

  let viewportTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(() => {
    const px = panX();
    const py = panY();
    const z = zoom();
    const W = w();
    const H = h();
    if (typeof window === "undefined") return;
    if (!props.onViewportChange) return;
    if (viewportTimer) clearTimeout(viewportTimer);
    viewportTimer = setTimeout(() => {
      const worldCx = -px / z;
      const worldCy = -py / z;
      const worldHalf = Math.max(W, H) / (2 * z);
      props.onViewportChange?.({ worldCx, worldCy, worldHalf, zoom: z });
    }, 200);
  });

  const resetView = () => {
    setPanX(0);
    setPanY(0);
    setZoom(1);
    try {
      window.localStorage.removeItem(VIEW_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  onMount(() => {
    window.addEventListener("resize", onResize);
    window.addEventListener(RESET_EVENT, resetView);
    window.addEventListener("keydown", onKeyDown);
    rafId = requestAnimationFrame(tick);
    const hintTimer = window.setTimeout(() => {
      if (!navHintDismissed) setShowNavHint(true);
    }, HINT_IDLE_MS);
    onCleanup(() => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener(RESET_EVENT, resetView);
      window.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(hintTimer);
      if (saveTimer) clearTimeout(saveTimer);
      if (viewportTimer) clearTimeout(viewportTimer);
    });
  });

  return (
    <>
      <canvas
        ref={canvas}
        class="absolute inset-0 w-full h-full touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        onWheel={onWheel}
        onDblClick={onDblClick}
      />
      <div
        class="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-slate-950/70 px-4 py-2 text-xs text-amber-100 backdrop-blur-sm transition-opacity duration-500"
        classList={{ "opacity-0": !showNavHint(), "opacity-100": showNavHint() }}
        data-testid="nav-hint"
        aria-hidden={!showNavHint()}
      >
        {t("navHint")}
      </div>
    </>
  );
}
